import "./UsageMeter.scss";

import { ExclamationTriangleIcon, Text, Tooltip } from "@fluentui/react-northstar";
import { UserDTO } from "@teams/types/api";
import React from "react";

import { getPlanLevelDisplayText } from "../../utils/paymentHelpers";
import { shouldShowResponseTrackingMeter } from "../../utils/userHelpers";
import { getTrialDaysLeftText } from "../../utils/userHelpers";
import { Button } from "../Button";
import { PollyIcon } from "../PollyIcon";
import { MeterBar } from "./MeterBar";

const LEARN_MORE_LINK = "https://www.polly.ai/help/microsoft-teams/response-limitations-explained";

interface Props {
  user: UserDTO;
  onLearnMore?: () => void;
  alwaysVisible?: boolean;
  displayMode?: "regular" | "standalone" | "compact";
  hideCta?: boolean;
}

export const UsageMeter: React.FC<Props> = ({
  user,
  alwaysVisible,
  displayMode = "regular",
  hideCta,
}) => {
  const usagePercentage = (user.usage / user.featureFlags.responseUsageLimit) * 100;
  const trialSub = user.subscriptions.find(s => s.type === "trial");

  if (!shouldShowResponseTrackingMeter(user) && !alwaysVisible) {
    return null;
  }

  const onLearnMore = () => {
    window.open(LEARN_MORE_LINK, "_blank");
  };

  const getLabel = () => {
    return (
      <div className="UsageMeter__label-container">
        <ExclamationTriangleIcon outline xSpacing="after" styles={{ marginBottom: "2px" }} />
        <Text className="UsageMeter__label-container__label">
          {user.usage}/{getPlanLevelDisplayText(user.featureFlags.responseUsageLimit)}
          {displayMode === "compact" ? "" : " Responses collected"}
        </Text>
      </div>
    );
  };

  return (
    <div className={`UsageMeter UsageMeter__${displayMode}`}>
      <div className="UsageMeter__meter-container">
        {usagePercentage >= 100 && displayMode === "compact" ? (
          <Tooltip content="Monthly response usage maxed">{getLabel()}</Tooltip>
        ) : (
          getLabel()
        )}
        <MeterBar
          progressPercentage={usagePercentage}
          warningPercentage={user.featureFlags.responseUsageWarningPercentage}
        />
      </div>
      <div className="UsageMeter__info-row">
        {trialSub && displayMode !== "compact" && (
          <span className="UsageMeter__trial-info-column">
            <span className="UsageMeter__trial-prefix">Trial:</span>
            <PollyIcon
              className="UsageMeter__trial-info"
              icon="trial-icon"
              primary
              ignoreTheme
              label={
                trialSub?.state === "active"
                  ? getTrialDaysLeftText(trialSub, user.featureFlags, true)
                  : "Ended"
              }
              labelPlacement="after"
            />
          </span>
        )}
        {displayMode !== "compact" && !hideCta && (
          <Button
            className="UsageMeter__upgrade-button"
            data-tname="meter-upgrade-survey"
            primary
            size="small"
            text
            content="Learn More"
            onClick={onLearnMore}
            styles={{ padding: 0 }}
          />
        )}
      </div>
    </div>
  );
};
